import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(() => ({
  orangeFocused: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: '#ffffff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 12,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#FF7F27',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputBase-input': {
      fontSize: 'clamp(0.72rem, calc(2px + 0.722vw), 1.0rem)',
      color: '#002D55',
    },
    '& .MuiInputLabel-root': {
      fontSize: 'clamp(0.65rem, calc(1px + 0.72vw), 0.9rem)',
    },
  },
  confirmButton: {
    backgroundColor: '#002D55',
    color: '#FFFFFF',
    borderRadius: 50,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(8px, 0.85vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  cancelButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 50,
    border: '2px solid transparent',
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(8px, 0.85vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '2px solid #FF7F27',
      boxShadow: 'none',
    },
  },
}));

const BASELINE_TYPE_OPTIONS = [
  { value: 'best', label: 'best/lowest' },
  { value: 'worst', label: 'worst/highest' },
  { value: 'current', label: 'current' },
  { value: 'rest', label: 'rest' },
  { value: 'activity', label: 'activity' },
  { value: 'frequency', label: 'frequency' },
  { value: 'difficulty', label: 'difficulty' },
  { value: 'custom', label: 'custom…' },
];

const BASELINE_LABELS = {
  frequency: 'Patient reported symptom:',
  best: 'Patient reported pain when at:',
  worst: 'Patient reported pain when at:',
  current: 'Patient reported pain as it is:',
  rest: 'Patient reported pain when at:',
  activity: 'Patient reported pain with:',
  difficulty: 'Patient reported:',
  custom: 'Patient reported:',
};

const BASELINE_SUFFIX = {
  best: 'best/lowest',
  worst: 'worst/highest',
  current: 'current',
  rest: 'rest',
  activity: 'activity',
  frequency: 'frequency',
  difficulty: 'difficulty',
  custom: '',
};

const THEME_MAP = {
  best: { theme: 'painAnamnesis_painAtBest', subgroup: 'painAtBest' },
  worst: { theme: 'painAnamnesis_painAtWorst', subgroup: 'painAtWorst' },
  current: { theme: 'painAnamnesis_painCurrent', subgroup: 'painCurrent' },
  frequency: { theme: 'painAnamnesis_frequency', subgroup: 'frequency' },
  rest: { theme: 'painAnamnesis_painAtRest', subgroup: 'painAtRest' },
  activity: { theme: 'painAnamnesis_painWithActivity', subgroup: 'painWithActivity' },
  difficulty: { theme: 'painAnamnesis_difficulty', subgroup: 'difficulty' },
  custom: { theme: 'painAnamnesis_custom', subgroup: 'custom' },
};

/**
 * Dialog mirroring the SelectedPatientDiagnosis assessment-tab "Add baseline /
 * Add goal" inline creators, but as a stand-alone dialog so it can be reused
 * from the Journal tab where the inline grouped sections don't exist.
 *
 * Saves by POSTing a single edit to
 *   /treatment/treatments/{treatmentId}/journal_versions/
 * with `mode: 'create_kpi'` so the backend creates a brand-new
 * pain_baseline / function_goal KpiMeasurement (matching how the Diagnosis
 * tab persists creator items).
 */
const AddBaselineGoalDialog = ({
  open,
  mode, // 'baseline' | 'goal'
  onClose,
  onSaved,
  onStage, // optional: when provided, dialog stages instead of POSTing
  initialItem, // optional: { id, mode, edit, lineText } — when provided, prefill for edit
  treatmentId,
  patientId,
  diagnosisIdentifier,
}) => {
  const tokenFromRedux = useSelector((state) => (state && state.auth && state.auth.token) || null);

  const classes = useStyles();

  const [baselineType, setBaselineType] = useState('best');
  const [score, setScore] = useState('');
  const [customMetric, setCustomMetric] = useState('');
  const [goalText, setGoalText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    // Defaults for fresh add
    let nextBaselineType = 'best';
    let nextScore = '';
    let nextCustomMetric = '';
    let nextGoalText = '';
    if (initialItem && initialItem.edit) {
      const ed = initialItem.edit;
      const numeric = parseInt(ed.numerical_value, 10);
      if (!Number.isNaN(numeric)) nextScore = String(numeric);
      if (initialItem.mode === 'goal') {
        nextGoalText = String(ed.journalPhrase || '').trim();
      } else {
        // Reverse THEME_MAP using kpi_subtype (camelCase subgroup) or theme.
        const subgroup = ed.kpi_subtype || ed.subgroup;
        const matched = Object.entries(THEME_MAP).find(([, v]) => v.subgroup === subgroup);
        if (matched) nextBaselineType = matched[0];
        if (nextBaselineType === 'custom') {
          // journalPhrase looks like: "Patient reported: <customMetric>"
          const phrase = String(ed.journalPhrase || '');
          const idx = phrase.indexOf(':');
          nextCustomMetric = idx >= 0 ? phrase.slice(idx + 1).trim() : phrase.trim();
        }
      }
    }
    setBaselineType(nextBaselineType);
    setScore(nextScore);
    setCustomMetric(nextCustomMetric);
    setGoalText(nextGoalText);
    setSaving(false);
    setError('');
  }, [open, mode, initialItem]);

  const isGoal = mode === 'goal';
  const isBaseline = mode === 'baseline';

  const scoreSuffix = useMemo(() => {
    if (isGoal) return ' / 100';
    if (baselineType === 'frequency') return ' / 7d';
    return ' / 100';
  }, [isGoal, baselineType]);

  const scoreMax = useMemo(() => {
    if (isGoal) return 100;
    if (baselineType === 'frequency') return 7;
    return 100;
  }, [isGoal, baselineType]);

  const buildBaselineEdit = () => {
    const themeInfo = THEME_MAP[baselineType] || THEME_MAP.current;
    const base = BASELINE_LABELS[baselineType] || 'Patient reported:';
    const suffix = baselineType === 'custom'
      ? (customMetric ? ` ${customMetric.trim()}` : '')
      : ` ${BASELINE_SUFFIX[baselineType] || ''}`;
    // Match Diagnosis-tab inline creator: insert ':' between the boilerplate
    // base and the option suffix when no terminator is already present, so
    // the inline-edit re-parser can split label/activity cleanly.
    const baseHasTerminator = /[:\u2014\u2013\-]\s*$/.test(base);
    const joinedBase = (suffix.trim() && !baseHasTerminator) ? `${base}:` : base;
    const labelText = `${joinedBase}${suffix}`.replace(/\s{2,}/g, ' ').trim();
    const id = `creator-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    return {
      id,
      theme: themeInfo.theme,
      subgroup: themeInfo.subgroup,
      metaJournalPhrase: '',
      journalPhrase: labelText,
      answer_text: '',
      numerical_value: String(parseInt(score, 10) || 0),
      mapped_key: themeInfo.theme,
      display_group: 'Pain baseline levels:',
      kpi_type: 'pain_baseline',
      // Diagnosis-tab buildFullSavePayload sets kpi_subtype = editVals.subgroup
      // for creator items (camelCase like 'painAtBest') — mirror exactly so
      // set_active_baseline routes to the canonical identifier.
      kpi_subtype: themeInfo.subgroup,
      mode: 'create_kpi',
      is_deleted: false,
      paused_from_followup: false,
    };
  };

  const buildGoalEdit = () => {
    const id = `creator-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    const trimmed = String(goalText || '').trim();
    const baseIdentifier = 'painAnamnesis_SelfRatedFunction_MainGoal';
    // Mirror Diagnosis-tab function-goal creator exactly: `_manual` suffix on
    // theme+subgroup so manual-goal grouping keys match downstream, canonical
    // mapped_key/identifier so backend routes to SelfRatedFunction_MainGoal,
    // and the `goal_text` answer_text_role so status-report / epicrisis keep
    // recognising it as a function goal. Score is /100 (PSFS scaled).
    return {
      id,
      theme: `${baseIdentifier}_manual`,
      subgroup: `${baseIdentifier}_manual`,
      metaJournalPhrase: 'Patient described functional goal (PSFS):',
      journalPhrase: trimmed,
      answer_text: '',
      answer_text_role: 'goal_text',
      numerical_value: String(parseInt(score, 10) || 0),
      numeric_value: parseInt(score, 10) || 0,
      mapped_key: baseIdentifier,
      identifier: baseIdentifier,
      display_group: 'Patient goal for treatment:',
      kpi_type: 'function_goal',
      kpi_subtype: `${baseIdentifier}_manual`,
      mode: 'create_kpi',
      is_deleted: false,
      paused_from_followup: false,
    };
  };

  const validate = () => {
    if (isGoal && !String(goalText || '').trim()) return 'Please enter the goal description.';
    if (isBaseline && baselineType === 'custom' && !String(customMetric || '').trim()) {
      return 'Please enter the custom metric name.';
    }
    const numeric = parseInt(score, 10);
    if (!score || Number.isNaN(numeric)) return 'Please enter a numeric score.';
    if (numeric < 0 || numeric > scoreMax) return `Score must be between 0 and ${scoreMax}.`;
    return '';
  };

  const buildLineText = (edit) => {
    const numeric = parseInt(score, 10) || 0;
    const suffix = isGoal
      ? '/100'
      : (baselineType === 'frequency' ? '/7d' : '/100');
    if (isGoal) {
      const goal = String(goalText || '').trim() || 'Goal';
      return `Added function goal: ${goal} = ${numeric}${suffix}`;
    }
    return `Added pain baseline: ${edit.journalPhrase} = ${numeric}${suffix}`;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!treatmentId) {
      setError('Missing treatment id.');
      return;
    }

    // Staged mode: build edit + line text, hand to parent, do not POST.
    if (typeof onStage === 'function') {
      const edit = isGoal ? buildGoalEdit() : buildBaselineEdit();
      const lineText = buildLineText(edit);
      try {
        onStage({ mode, edit, lineText, id: (initialItem && initialItem.id) || null });
      } catch (e) {
        setError(`Stage error: ${e && e.message ? e.message : 'unknown'}`);
        return;
      }
      if (onSaved) onSaved();
      if (onClose) onClose();
      return;
    }

    if (!tokenFromRedux) {
      setError('Not authenticated.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const edit = isGoal ? buildGoalEdit() : buildBaselineEdit();

      // POST a single journal version containing only the new edit. The
      // backend `?in_latest_journal=1` filter accumulates edit keys across
      // ALL prior versions, so adding one new baseline/goal here does NOT
      // drop the others. Re-asserting existing KPIs as `override_baseline`
      // would corrupt the audit chain — each override writes a fresh
      // KpiMeasurement at the current value, collapsing baseline ≡ latest
      // and flat-lining the chart / 0% improvement in epicrisis.
      const body = {
        treatment_id: treatmentId,
        user_id: patientId,
        note: isGoal
          ? 'Added function goal from journal tab'
          : 'Added pain baseline from journal tab',
        payload: { edits: [edit] },
      };
      const url = `https://api.virtus-one.com/treatment/treatments/${treatmentId}/journal_versions/`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Token ${tokenFromRedux}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let txt = '';
        try { txt = await res.text(); } catch (e) {}
        setError(`Save failed (${res.status}). ${txt || ''}`.trim());
        setSaving(false);
        return;
      }
      try { await res.json(); } catch (e) {}
      // Broadcast the same signal the Diagnosis tab uses so every listener
      // (SelectedPatientDiagnosis re-fetch, Learn_about_pain re-fetch, etc.)
      // refreshes against the new KPI rows.
      try {
        const updateDetail = { treatmentId, userId: patientId, ts: Date.now() };
        localStorage.setItem('vp_latest_journal_update', JSON.stringify(updateDetail));
        window.dispatchEvent(new CustomEvent('vp-journal-updated', { detail: updateDetail }));
      } catch (e) { /* ignore */ }
      setSaving(false);
      if (onSaved) onSaved();
      if (onClose) onClose();
    } catch (e) {
      setError(`Save error: ${e && e.message ? e.message : 'unknown'}`);
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 18 } }}>
      <DialogTitle style={{ backgroundColor: '#002D55', color: '#fff', padding: 'clamp(8px, 0.63vw, 12px) clamp(12px, 1.25vw, 24px)' }}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <span style={{ fontWeight: 600, fontSize: 'clamp(0.80rem, calc(4px + 0.722vw), 1.1rem)', letterSpacing: 1 }}>
            {initialItem
              ? (isGoal ? 'Edit patient goal' : 'Edit pain baseline')
              : (isGoal ? 'Add patient goal' : 'Add pain baseline')}
          </span>
        </Box>
      </DialogTitle>
      <DialogContent style={{ backgroundColor: '#f7faff', padding: 0 }}>
        <Paper elevation={0} style={{ padding: 'clamp(12px, 1.25vw, 24px)', background: '#F0F8FF', width: '100%', boxSizing: 'border-box', margin: 0 }}>
        {isBaseline && (
          <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Box>
              <Typography variant="body2" style={{ color: '#002D55', fontWeight: 600, marginBottom: 4, fontSize: 'clamp(0.65rem, calc(1px + 0.72vw), 0.9rem)' }}>
                Baseline type
              </Typography>
              <Select
                value={baselineType}
                onChange={(e) => setBaselineType(e.target.value)}
                variant="outlined"
                fullWidth
                classes={{ root: classes.orangeFocused }}
                style={{ borderRadius: 12, backgroundColor: '#ffffff', color: '#002D55', fontSize: 'clamp(0.72rem, calc(2px + 0.722vw), 1.0rem)' }}
              >
                {BASELINE_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </Box>
            {baselineType === 'custom' && (
              <Box>
                <Typography variant="body2" style={{ color: '#002D55', fontWeight: 600, marginBottom: 4, fontSize: 'clamp(0.65rem, calc(1px + 0.72vw), 0.9rem)' }}>
                  Custom metric name
                </Typography>
                <TextField
                  value={customMetric}
                  onChange={(e) => setCustomMetric(e.target.value)}
                  placeholder="e.g. pain with bench press"
                  fullWidth
                  variant="outlined"
                  size="small"
                  classes={{ root: classes.orangeFocused }}
                />
              </Box>
            )}
          </Box>
        )}
        {isGoal && (
          <Box>
            <Typography variant="body2" style={{ color: '#002D55', fontWeight: 600, marginBottom: 4, fontSize: 'clamp(0.65rem, calc(1px + 0.72vw), 0.9rem)' }}>
              Goal description
            </Typography>
            <TextField
              value={goalText}
              onChange={(e) => setGoalText(e.target.value)}
              placeholder="e.g. Walk 30 minutes pain-free"
              fullWidth
              variant="outlined"
              size="small"
              multiline
              rowsMax={3}
              classes={{ root: classes.orangeFocused }}
            />
          </Box>
        )}
        <Box style={{ marginTop: 16 }}>
          <Typography variant="body2" style={{ color: '#002D55', fontWeight: 600, marginBottom: 4, fontSize: 'clamp(0.65rem, calc(1px + 0.72vw), 0.9rem)' }}>
            Score
          </Typography>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TextField
              value={score}
              onChange={(e) => setScore(e.target.value)}
              type="number"
              variant="outlined"
              size="small"
              inputProps={{ min: 0, max: scoreMax }}
              style={{ width: 120 }}
              classes={{ root: classes.orangeFocused }}
            />
            <Typography variant="body2" style={{ color: '#5f7287', fontSize: 'clamp(0.65rem, calc(1px + 0.72vw), 0.9rem)' }}>{scoreSuffix}</Typography>
          </Box>
        </Box>
        {error && (
          <Typography variant="body2" style={{ color: '#c62828', marginTop: 12, fontSize: 'clamp(0.65rem, calc(1px + 0.72vw), 0.9rem)' }}>{error}</Typography>
        )}
        </Paper>
      </DialogContent>
      <DialogActions style={{ backgroundColor: '#f7faff', padding: 'clamp(8px, 0.63vw, 12px) clamp(12px, 1.25vw, 24px)' }}>
        <Button onClick={onClose} disabled={saving} variant="contained" className={classes.cancelButton}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          className={classes.confirmButton}
        >
          {saving ? <CircularProgress size={18} style={{ color: '#fff' }} /> : (initialItem ? 'Update' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBaselineGoalDialog;
